import type { Claim, Provenance } from '../../../types/report';
import { mediaSrc } from '../mediaSrc';

/** Actions the panel can invoke (threaded from DrillPanel via useReportPanel).
 * Kept optional so the panels still render in isolation (e.g. unit tests). */
export interface PanelActions {
  openClaim?: (claimId: string) => void; // replace panel content with another claim
  openChat?: (claimId: string) => void; // switch this panel to Chat mode, scoped
  scrollToSection?: (sectionId: string) => void; // close panel + scroll report
}

const TIER_LABEL: Record<string, string> = {
  measured: 'Measured',
  modeled: 'Modeled',
  directional: 'Directional',
};
const TIER_NOTE: Record<string, string> = {
  measured: 'A direct survey aggregate (n ≥ 30). Read as a reliable population estimate.',
  modeled:
    'Derived from other measured figures (e.g. size × rate). Directionally sound, not a raw count.',
  directional: 'Based on a small base or partial data. Treat as a signal, not a definitive number.',
};

/** Synthetic claim id for a confidence-tier explainer drill (PRD §9.2). The
 * DrillPanel recognises this prefix and renders a Type-D tier panel. */
export const TIER_DRILL_PREFIX = '__tier__:';

/** Confidence-tier badge. Clickable → opens a Type-D drill explaining the tier
 * (PRD §9.2). Falls back to a static pill when no openClaim action is provided. */
function Badge({ tier, actions }: { tier: string; actions?: PanelActions }) {
  const label = TIER_LABEL[tier] ?? tier;
  if (actions?.openClaim) {
    return (
      <button
        type="button"
        className={`panel-badge ${tier} panel-badge-clickable`}
        onClick={() => actions.openClaim!(`${TIER_DRILL_PREFIX}${tier}`)}
        aria-label={`What ${label} means`}
      >
        {label}
      </button>
    );
  }
  return <span className={`panel-badge ${tier}`}>{label}</span>;
}

function Header({ claim, actions }: { claim: Claim; actions?: PanelActions }) {
  return (
    <div className="panel-head">
      <div className="panel-eyebrow">{claim.header.eyebrow ?? 'Source detail'}</div>
      <div className="panel-title">{claim.header.title}</div>
      {claim.header.value && (
        <div className="panel-value-row">
          <span className="panel-value">{claim.header.value}</span>
          <Badge tier={claim.tier} actions={actions} />
        </div>
      )}
      <div className={`panel-tier-note ${claim.tier}`}>{TIER_NOTE[claim.tier]}</div>
      {claim.filtered?.activeFilter && (
        <div className="panel-filtered" data-testid="filtered-tag">
          Filtered: {claim.filtered.activeFilter}
          {claim.filtered.fullSampleValue && <> · full sample {claim.filtered.fullSampleValue}</>}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="panel-field">
      <div className="panel-field-label">{label}</div>
      <div className="panel-field-body">{children}</div>
    </div>
  );
}

function Verbatim({ text, attr }: { text: string; attr?: string }) {
  return (
    <div className="panel-verbatim">
      “{text}”{attr && <span className="panel-verbatim-attr">{attr}</span>}
    </div>
  );
}

/** demoSections — grouped demographic bar breakdowns. */
function DemoSections({ claim }: { claim: Claim }) {
  const sections = (claim.demoSections ?? []) as Array<Record<string, unknown>>;
  if (sections.length === 0) return null;
  return (
    <>
      {sections.map((sec, si) => {
        const items = (sec.items as Array<Record<string, unknown>>) || [];
        return (
          <div key={si} className="panel-field">
            <div className="panel-field-label">{String(sec.label ?? '')}</div>
            <div className="panel-breakdown">
              {items.map((it, ii) => {
                const width = typeof it.width === 'number' ? it.width : undefined;
                return (
                  <div key={ii} className="panel-bar-row">
                    <div className="panel-bar-head">
                      <span className="panel-bar-label">{String(it.label ?? '')}</span>
                      <span className="panel-mono">{String(it.value ?? '')}</span>
                    </div>
                    {width !== undefined && (
                      <div className="panel-bar-track">
                        <div className="panel-bar-fill" style={{ width: `${width}%` }} />
                      </div>
                    )}
                    {it.note ? <div className="panel-bar-note">{String(it.note)}</div> : null}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}

/** Analyst note — the interpretive "what this means" line(s) (claim.commentary). */
function AnalystNote({ claim }: { claim: Claim }) {
  const notes = (claim.commentary ?? []).filter(Boolean);
  if (notes.length === 0) return null;
  return (
    <div className="panel-field">
      <div className="panel-field-label">Analyst note</div>
      <div className="panel-field-body">
        {notes.map((n, i) => (
          <p key={i} className="panel-para">
            {n}
          </p>
        ))}
      </div>
    </div>
  );
}

/** Contextual tags — verbatim coding / theme labels (chips). */
function ContextualTags({ claim }: { claim: Claim }) {
  const tags = (claim.contextualTags ?? []).filter(Boolean);
  if (tags.length === 0) return null;
  return (
    <div className="panel-field">
      <div className="panel-field-label">Coded themes</div>
      <div className="panel-tags">
        {tags.map((t, i) => (
          <span key={i} className="panel-tag">
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Related in this report — cross-reference chips. PRD §10 behavior split:
 *  • a related CLAIM chip ({ claimId }) REPLACES the panel content in place;
 *  • a related SECTION chip ({ sectionId }) CLOSES the panel and SCROLLS the
 *    report to that section.
 * Falls back to an anchor link when no actions are wired (isolation/tests). */
function RelatedLinks({ claim, actions }: { claim: Claim; actions?: PanelActions }) {
  const related = (claim.related ?? []) as Array<Record<string, unknown>>;
  if (related.length === 0) return null;
  return (
    <div className="panel-field">
      <div className="panel-field-label">Related in this report</div>
      <div className="panel-related">
        {related.map((r, i) => {
          const label = String(r.label ?? '');
          const claimId = r.claimId ? String(r.claimId) : undefined;
          const sid = r.sectionId ? String(r.sectionId) : undefined;
          if (claimId && actions?.openClaim) {
            return (
              <button
                key={i}
                type="button"
                className="panel-related-item"
                onClick={() => actions.openClaim!(claimId)}
              >
                {label}
              </button>
            );
          }
          if (sid && actions?.scrollToSection) {
            return (
              <button
                key={i}
                type="button"
                className="panel-related-item"
                onClick={() => actions.scrollToSection!(sid)}
              >
                {label}
              </button>
            );
          }
          return sid ? (
            <a key={i} className="panel-related-item" href={`#${sid}`}>
              {label}
            </a>
          ) : (
            <span key={i} className="panel-related-item">
              {label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** Mini-stats grid — 2-4 supporting numbers for chart/matrix drills (PRD §5.2).
 * Data: claim.miniStats = [{ value, label }]. */
function MiniStats({ claim }: { claim: Claim }) {
  const stats = (claim.miniStats ?? []) as Array<Record<string, unknown>>;
  if (stats.length === 0) return null;
  return (
    <div className="panel-ministats">
      {stats.map((s, i) => (
        <div key={i} className="panel-ministat">
          <div className="panel-ministat-value">{String(s.value ?? '')}</div>
          <div className="panel-ministat-label">{String(s.label ?? '')}</div>
        </div>
      ))}
    </div>
  );
}

/** Ask CTA — mandatory footer on every panel (PRD §4.2-F, §11). Switches the
 * panel to Chat mode, scoped to this claim. Omitted only if no openChat action. */
function AskCta({ claim, actions }: { claim: Claim; actions?: PanelActions }) {
  if (!actions?.openChat) return null;
  return (
    <button
      type="button"
      className="panel-ask-cta"
      data-testid="ask-cta"
      onClick={() => actions.openChat!(claim.id)}
    >
      Ask about this
    </button>
  );
}

/** Provenance — every quant claim carries where it came from. This is what
 * makes the panel "thick": the survey question, who was counted, the base n,
 * and the exact recompute recipe. */
function ProvenanceBlock({ prov }: { prov: Provenance }) {
  const baseN = prov.baseIds?.length;
  const matchN = prov.respondentIds?.length;
  const c = prov.compute;
  const recipe = c ? `${c.op}(${c.field})${c.denominator ? ` ÷ ${c.denominator}` : ''}` : null;
  return (
    <div className="panel-provenance">
      <div className="panel-field-label">Traceability</div>
      <div className="panel-prov-grid">
        <span>Respondent scope</span>
        <span>{prov.respondentScope}</span>
        {typeof baseN === 'number' && (
          <>
            <span>Base (denominator)</span>
            <span>{baseN} respondents</span>
          </>
        )}
        {typeof matchN === 'number' && (
          <>
            <span>Matched (numerator)</span>
            <span>{matchN} respondents</span>
          </>
        )}
        {recipe && (
          <>
            <span>Compute</span>
            <span className="panel-mono">{recipe}</span>
          </>
        )}
      </div>
    </div>
  );
}

/** Survey question · source of this number (mock's source-question block). */
function SourceQuestion({ claim }: { claim: Claim }) {
  const sq = claim.surveyQuestion as { text?: string } | null;
  if (!sq?.text) return null;
  return (
    <div className="panel-field">
      <div className="panel-field-label">Survey question · source of this number</div>
      <div className="panel-source-question">{sq.text}</div>
    </div>
  );
}

/** Type A — Source panel. */
export function SourcePanel({ claim, actions }: { claim: Claim; actions?: PanelActions }) {
  const v = claim.verbatim as { text?: string; attribution?: string } | null;
  return (
    <div data-panel="source" className="panel-doc">
      <Header claim={claim} actions={actions} />
      {claim.definition && <Field label="Definition">{claim.definition}</Field>}
      {claim.method && <Field label="Method">{claim.method}</Field>}
      <SourceQuestion claim={claim} />
      {claim.breakdown && claim.breakdown.length > 0 && (
        <Field label="How the segments compare">
          <div className="panel-breakdown">
            {claim.breakdown.map((row, i) => {
              const r = row as Record<string, unknown>;
              const label = String(r.label ?? '');
              const value = String(r.value ?? '');
              const width = typeof r.width === 'number' ? r.width : undefined;
              return (
                <div key={i} className="panel-bar-row">
                  <div className="panel-bar-head">
                    <span className="panel-bar-label">{label}</span>
                    <span className="panel-mono">{value}</span>
                  </div>
                  {width !== undefined && (
                    <div className="panel-bar-track">
                      <div className="panel-bar-fill" style={{ width: `${width}%` }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Field>
      )}
      <DemoSections claim={claim} />
      {v?.text && <Verbatim text={v.text} attr={v.attribution} />}
      <AnalystNote claim={claim} />
      {claim.provenance && <ProvenanceBlock prov={claim.provenance} />}
      <RelatedLinks claim={claim} actions={actions} />
      <AskCta claim={claim} actions={actions} />
    </div>
  );
}

/** Types B/C — Commentary panel. Chart (B) and matrix (C) drills: analyst lede +
 * mini-stats + contextual tags + interpretation paragraphs (PRD §5.2, §6.2). */
export function CommentaryPanel({ claim, actions }: { claim: Claim; actions?: PanelActions }) {
  const rc = claim.rowColContext as { row?: string; col?: string; indicator?: string } | null;
  return (
    <div data-panel="commentary" className="panel-doc">
      <Header claim={claim} actions={actions} />
      {rc && (rc.row || rc.col) && (
        <div className="panel-rowcol">
          {String(rc.row ?? '')} × {String(rc.col ?? '')}
        </div>
      )}
      {rc?.indicator && <div className="panel-rowcol-indicator">{String(rc.indicator)}</div>}
      {claim.lede && <p className="panel-lede">{claim.lede}</p>}
      <MiniStats claim={claim} />
      <ContextualTags claim={claim} />
      {(claim.commentary ?? []).map((p, i) => (
        <p key={i} className="panel-para">
          {p}
        </p>
      ))}
      {claim.definition && <Field label="Definition">{claim.definition}</Field>}
      <SourceQuestion claim={claim} />
      {claim.provenance && <ProvenanceBlock prov={claim.provenance} />}
      <RelatedLinks claim={claim} actions={actions} />
      <AskCta claim={claim} actions={actions} />
    </div>
  );
}

/** Type D — Definition panel. */
export function DefinitionPanel({ claim, actions }: { claim: Claim; actions?: PanelActions }) {
  const entity = (claim.entity ?? {}) as Record<string, unknown>;
  return (
    <div data-panel="definition" className="panel-doc">
      <Header claim={claim} actions={actions} />
      {claim.definition && <Field label="Definition">{claim.definition}</Field>}
      {entity.categoryContext ? (
        <Field label="Category context">{String(entity.categoryContext)}</Field>
      ) : null}
      <SourceQuestion claim={claim} />
      {claim.provenance && <ProvenanceBlock prov={claim.provenance} />}
      <RelatedLinks claim={claim} actions={actions} />
      <AskCta claim={claim} actions={actions} />
    </div>
  );
}

/** Type E — Evidence / verbatim panel. */
export function EvidencePanel({ claim, actions }: { claim: Claim; actions?: PanelActions }) {
  const media = (claim.media ?? {}) as Record<string, unknown>;
  const v = (claim.verbatim ?? {}) as { text?: string; attribution?: string };
  return (
    <div data-panel="evidence" className="panel-doc">
      <Header claim={claim} actions={actions} />
      {media.url && media.kind === 'image' ? (
        <img
          src={mediaSrc(String(media.url))}
          alt={String(media.description ?? '')}
          className="panel-media"
        />
      ) : null}
      {v.text && <Verbatim text={v.text} attr={v.attribution} />}
      {(media.whatItTellsUs || (claim.commentary && claim.commentary[0])) && (
        <Field label="What it tells us">
          {String(media.whatItTellsUs ?? claim.commentary![0])}
        </Field>
      )}
      {claim.definition && <Field label="How this was selected">{claim.definition}</Field>}
      <ContextualTags claim={claim} />
      <SourceQuestion claim={claim} />
      {claim.provenance && <ProvenanceBlock prov={claim.provenance} />}
      <RelatedLinks claim={claim} actions={actions} />
      <AskCta claim={claim} actions={actions} />
    </div>
  );
}

/** Synthetic Type-D panel explaining a confidence tier (PRD §9.2). Rendered when
 * a tier badge is clicked; content is static + a couple of example claims. */
export function TierPanel({
  tier,
  examples,
  actions,
}: {
  tier: string;
  examples?: Array<{ id: string; title: string }>;
  actions?: PanelActions;
}) {
  const label = TIER_LABEL[tier] ?? tier;
  const criteria: Record<string, string> = {
    measured:
      'Assigned to any claim that is a simple aggregate of survey responses ' +
      '(percentages, means, medians) over a base of at least 30 respondents.',
    modeled:
      'Assigned to values derived through analysis — clustering, regression, ' +
      'or index construction — rather than read directly off one survey question.',
    directional:
      'Assigned when the base is small (under ~30), the data is partial, ' +
      'or the pattern is qualitative and inferred rather than statistically supported.',
  };
  return (
    <div data-panel="definition" className="panel-doc" data-tier-panel={tier}>
      <div className="panel-head">
        <div className="panel-eyebrow">Confidence tier</div>
        <div className="panel-title">{label}</div>
        <div className="panel-value-row">
          <span className={`panel-badge ${tier}`}>{label}</span>
        </div>
        <div className={`panel-tier-note ${tier}`}>{TIER_NOTE[tier]}</div>
      </div>
      <Field label="What it means">{TIER_NOTE[tier]}</Field>
      <Field label="How we assign this tier">{criteria[tier] ?? ''}</Field>
      {examples && examples.length > 0 && (
        <div className="panel-field">
          <div className="panel-field-label">Example claims at this tier</div>
          <div className="panel-related">
            {examples.map((e) => (
              <button
                key={e.id}
                type="button"
                className="panel-related-item"
                onClick={() => actions?.openClaim?.(e.id)}
              >
                {e.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function panelForType(claim: Claim, actions?: PanelActions) {
  switch (claim.type) {
    case 'A':
      return <SourcePanel claim={claim} actions={actions} />;
    case 'B':
    case 'C':
      return <CommentaryPanel claim={claim} actions={actions} />;
    case 'D':
      return <DefinitionPanel claim={claim} actions={actions} />;
    case 'E':
      return <EvidencePanel claim={claim} actions={actions} />;
    default:
      return <SourcePanel claim={claim} actions={actions} />;
  }
}
